/**
 * Commentary tab — a clean event timeline built from the match events we
 * already have (goals, cards, subs, VAR, penalties). "All" vs "Highlights".
 */
import { useState, useMemo } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const isHighlight = (e) => {
  const t = (e.type || '').toLowerCase();
  const d = (e.detail || '').toLowerCase();
  return t === 'goal' || t === 'card' || t === 'var' || d.includes('red card') || d.includes('penalty');
};

const line = (e, homeTeam, awayTeam) => {
  const team = e.team_id ? (e.team_side === 'home' ? homeTeam : awayTeam) : '';
  const who = e.player || 'Player';
  const t = (e.type || '').toLowerCase();
  const d = e.detail || '';
  if (t === 'goal') {
    if ((d || '').toLowerCase().includes('own')) return `Own goal by ${who}.`;
    if ((d || '').toLowerCase().includes('penalty')) return `GOAL! ${who} scores from the penalty spot${team ? ` for ${team}` : ''}.${e.assist ? '' : ''}`;
    return `GOAL! ${who} finds the net${team ? ` for ${team}` : ''}.${e.assist ? ` Assisted by ${e.assist}.` : ''}`;
  }
  if (t === 'card') return `${d || 'Card'} shown to ${who}.`;
  if (t === 'subst') return `Substitution${team ? ` (${team})` : ''}: ${who} comes on${e.assist ? `, ${e.assist} off` : ''}.`;
  if (t === 'var') return `VAR: ${d || 'review'}${who ? ` — ${who}` : ''}.`;
  return `${d || e.type || 'Event'}${who ? ` — ${who}` : ''}.`;
};

const dotColor = (e) => {
  const t = (e.type || '').toLowerCase();
  const d = (e.detail || '').toLowerCase();
  if (t === 'goal') return 'bg-emerald-400';
  if (d.includes('red')) return 'bg-red-500';
  if (t === 'card') return 'bg-yellow-400';
  if (t === 'var') return 'bg-purple-400';
  return 'bg-zinc-500';
};

// Prominent chip (LiveScore-style) for the standout events.
const eventBadge = (e) => {
  const t = (e.type || '').toLowerCase();
  const d = (e.detail || '').toLowerCase();
  if (t === 'var') return { label: 'VAR', cls: 'bg-purple-500/20 text-purple-300 ring-1 ring-purple-500/40' };
  if (d.includes('red') || d.includes('second yellow')) return { label: 'Red Card', cls: 'bg-red-500/20 text-red-300 ring-1 ring-red-500/40' };
  if (t === 'goal') return { label: d.includes('penalty') ? 'Goal · Pen' : (d.includes('own') ? 'Own Goal' : 'Goal'), cls: 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/40' };
  if (t === 'card') return { label: 'Yellow', cls: 'bg-yellow-500/20 text-yellow-300 ring-1 ring-yellow-500/40' };
  return null;
};

const MatchCommentary = ({ events = [], homeTeam, awayTeam, homeId }) => {
  const [filter, setFilter] = useState('all');
  const withSide = useMemo(
    () => (events || []).map((e) => ({ ...e, team_side: e.team_id === homeId ? 'home' : 'away' })),
    [events, homeId],
  );
  const rows = useMemo(() => {
    const list = filter === 'highlights' ? withSide.filter(isHighlight) : withSide;
    // newest first
    return [...list].reverse();
  }, [withSide, filter]);

  return (
    <div className="px-3 pb-6" data-testid="commentary-panel">
      <div className="flex gap-2 py-3">
        {['all', 'highlights'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`commentary-filter-${f}`}
            className={`px-4 py-1.5 rounded-full text-[12px] font-bold capitalize transition-colors ${
              filter === f ? 'bg-accent text-white' : 'bg-white/5 text-zinc-400'
            }`}
          >
            {f}
          </button>
        ))}
      </div>
      {rows.length === 0 ? (
        <div className="rounded-2xl bg-[var(--sk-surface)] p-5 text-[12px] text-zinc-500 text-center">
          No {filter === 'highlights' ? 'highlights' : 'commentary'} yet — this fills in live as the match unfolds.
        </div>
      ) : (
        <div className="space-y-2.5">
          {rows.map((e, i) => {
            const minute = `${e.time ?? ''}${e.extra_time ? '+' + e.extra_time : ''}'`;
            const photo = e.player_id ? `${API_URL}/api/proxy/player-image/${e.player_id}` : null;
            return (
              <div key={i} className="flex items-start gap-3 rounded-2xl bg-[var(--sk-surface)] p-3.5" data-testid={`commentary-row-${i}`}>
                <div className="flex flex-col items-center shrink-0 w-11">
                  <span className="text-[12px] font-black text-white tabular-nums">{minute}</span>
                  <span className={`mt-1 w-2 h-2 rounded-full ${dotColor(e)}`} />
                </div>
                {photo && (
                  <img src={photo} alt="" className="w-9 h-9 rounded-full object-cover bg-white/5 shrink-0" loading="lazy"
                       onError={(ev) => { ev.currentTarget.style.display = 'none'; }} />
                )}
                <div className="flex-1 min-w-0">
                  {eventBadge(e) && (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider mb-1 ${eventBadge(e).cls}`} data-testid={`commentary-badge-${i}`}>
                      {eventBadge(e).label}
                    </span>
                  )}
                  <p className="text-[13px] text-zinc-200 leading-relaxed">{line(e, homeTeam, awayTeam)}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MatchCommentary;
